import React, { useCallback, useState, useContext } from 'react';
import { AnnotationTypes } from '@atlaskit/adf-schema';
import { updateWindowSelectionAroundDraft } from '../draft';
import { FabricChannel } from '@atlaskit/analytics-listeners';
import { ACTION, ACTION_SUBJECT, EVENT_TYPE, ACTION_SUBJECT_ID } from '@atlaskit/editor-common/analytics';
import { RendererContext as ActionsContext } from '../../RendererActionsContext';
import { ProvidersContext } from '../context';
export const Mounter = /*#__PURE__*/React.memo(props => {
  const {
    component: Component,
    range,
    isWithinRange,
    isAnnotationAllowed,
    wrapperDOM,
    onClose: onCloseProps,
    clearAnnotationDraft,
    applyAnnotationDraftAt,
    documentPosition,
    applyAnnotation,
    createAnalyticsEvent,
    generateIndexMatch
  } = props;
  const [draftDocumentPosition, setDraftDocumentPosition] = useState();
  const actions = useContext(ActionsContext);
  const providers = useContext(ProvidersContext);
  const isCommentsOnMediaBugFixEnabled = Boolean(providers === null || providers === void 0 ? void 0 : providers.inlineComment.isCommentsOnMediaBugFixEnabled);
  const onCreateCallback = useCallback(annotationId => {
    if (!isAnnotationAllowed || !documentPosition || !applyAnnotation) {
      return false;
    }
    const annotation = {
      annotationId,
      annotationType: AnnotationTypes.INLINE_COMMENT
    };
    if (createAnalyticsEvent) {
      createAnalyticsEvent({
        action: ACTION.INSERTED,
        actionSubject: ACTION_SUBJECT.ANNOTATION,
        actionSubjectId: ACTION_SUBJECT_ID.INLINE_COMMENT,
        attributes: {},
        eventType: EVENT_TYPE.TRACK
      }).fire(FabricChannel.editor);
    }
    return applyAnnotation(draftDocumentPosition || documentPosition, annotation, isCommentsOnMediaBugFixEnabled);
  }, [isAnnotationAllowed, documentPosition, applyAnnotation, draftDocumentPosition, createAnalyticsEvent, isCommentsOnMediaBugFixEnabled]);
  const createIndexCallback = useCallback(() => {
    if (!documentPosition || !generateIndexMatch) {
      return false;
    }
    const result = generateIndexMatch(documentPosition);
    if (!result) {
      return false;
    }
    return result;
  }, [documentPosition, generateIndexMatch]);
  const applyDraftModeCallback = useCallback(options => {
    if (!documentPosition || !isAnnotationAllowed) {
      if (createAnalyticsEvent) {
        createAnalyticsEvent({
          action: ACTION.CREATE_NOT_ALLOWED,
          actionSubject: ACTION_SUBJECT.ANNOTATION,
          actionSubjectId: ACTION_SUBJECT_ID.INLINE_COMMENT,
          attributes: {},
          eventType: EVENT_TYPE.TRACK
        }).fire(FabricChannel.editor);
      }
      return false;
    }
    setDraftDocumentPosition(documentPosition);
    applyAnnotationDraftAt(documentPosition);
    if (createAnalyticsEvent) {
      const uniqueAnnotationsInRange = actions.getAnnotationsByPosition(range);
      createAnalyticsEvent({
        action: ACTION.OPENED,
        actionSubject: ACTION_SUBJECT.ANNOTATION,
        actionSubjectId: ACTION_SUBJECT_ID.INLINE_COMMENT,
        eventType: EVENT_TYPE.TRACK,
        attributes: {
          overlap: uniqueAnnotationsInRange.length
        }
      }).fire(FabricChannel.editor);
    }
    window.requestAnimationFrame(() => {
      if (options.keepNativeSelection) {
        updateWindowSelectionAroundDraft(documentPosition);
      } else {
        const sel = window.getSelection();
        if (sel) {
          sel.removeAllRanges();
        }
      }
    });
    const positionToAnnotate = draftDocumentPosition || documentPosition;
    if (!positionToAnnotate || !applyAnnotation || !options.annotationId) {
      return false;
    }
    const annotation = {
      annotationId: options.annotationId,
      annotationType: AnnotationTypes.INLINE_COMMENT
    };
    return applyAnnotation(positionToAnnotate, annotation, isCommentsOnMediaBugFixEnabled);
  }, [documentPosition, isAnnotationAllowed, applyAnnotationDraftAt, createAnalyticsEvent, applyAnnotation, draftDocumentPosition, isCommentsOnMediaBugFixEnabled, actions, range]);
  const removeDraftModeCallback = useCallback(() => {
    clearAnnotationDraft();
    setDraftDocumentPosition(null);
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
    }
  }, [clearAnnotationDraft]);
  const onCloseCallback = useCallback(() => {
    if (createAnalyticsEvent) {
      createAnalyticsEvent({
        action: ACTION.CLOSED,
        actionSubject: ACTION_SUBJECT.ANNOTATION,
        actionSubjectId: ACTION_SUBJECT_ID.INLINE_COMMENT,
        eventType: EVENT_TYPE.TRACK,
        attributes: {}
      }).fire(FabricChannel.editor);
    }
    removeDraftModeCallback();
    onCloseProps();
  }, [onCloseProps, removeDraftModeCallback, createAnalyticsEvent]);
  return /*#__PURE__*/React.createElement(Component, {
    range: range,
    isWithinRange: isWithinRange,
    wrapperDOM: wrapperDOM.current,
    isAnnotationAllowed: isAnnotationAllowed,
    onClose: onCloseCallback,
    onCreate: onCreateCallback,
    getAnnotationIndexMatch: createIndexCallback,
    applyDraftMode: applyDraftModeCallback,
    removeDraftMode: removeDraftModeCallback
  });
});